import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, differenceInDays } from "date-fns";
import { RefreshCw, ClipboardList, Filter, CheckCircle2, Search, HelpCircle, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get stored credentials from sessionStorage
const getAuthHeader = () => {
  const creds = sessionStorage.getItem('lyca_auth');
  if (creds) {
    return { Authorization: `Basic ${creds}` };
  }
  return {};
};

export default function RenewalTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({
    customer_name: "",
    lyca_mobile_number: "",
    plan_type_name: "",
    plan_price: "",
    renewal_date: "",
  });
  const [saving, setSaving] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTask, setDeletingTask] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      const endpoint = filter === "pending" ? "/renewal-tasks/pending" : "/renewal-tasks";
      const tasksRes = await axios.get(`${API}${endpoint}`, { headers });
      setTasks(tasksRes.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load renewal tasks");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleMarkRenewed = async (taskId, isRenewed) => {
    try {
      const headers = getAuthHeader();
      await axios.put(`${API}/renewal-tasks/${taskId}/renew`, { is_renewed: isRenewed }, { headers });
      toast.success(isRenewed ? "Plan marked as renewed! Next renewal scheduled in 27 days." : "Task marked as pending");
      fetchTasks();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleGenerateTasks = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.post(`${API}/tasks/generate`, {}, { headers });
      toast.success(response.data.message);
      fetchTasks();
    } catch (error) {
      console.error("Error generating tasks:", error);
      toast.error("Failed to generate tasks");
    }
  };

  // Edit handlers
  const handleEditClick = (task) => {
    setEditingTask(task);
    setEditForm({
      customer_name: task.customer_name,
      lyca_mobile_number: task.lyca_mobile_number,
      plan_type_name: task.plan_type_name,
      plan_price: task.plan_price.toString(),
      renewal_date: format(new Date(task.renewal_date), "yyyy-MM-dd"),
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      setSaving(true);
      const headers = getAuthHeader();
      await axios.put(`${API}/renewal-tasks/${editingTask.id}`, {
        customer_name: editForm.customer_name,
        lyca_mobile_number: editForm.lyca_mobile_number,
        plan_type_name: editForm.plan_type_name,
        plan_price: parseFloat(editForm.plan_price),
        renewal_date: new Date(editForm.renewal_date).toISOString(),
      }, { headers });
      toast.success("Task updated successfully");
      setEditDialogOpen(false);
      fetchTasks();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = (task) => {
    setDeletingTask(task);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const headers = getAuthHeader();
      await axios.delete(`${API}/renewal-tasks/${deletingTask.id}`, { headers });
      toast.success("Task deleted successfully");
      setDeleteDialogOpen(false);
      fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  const getDaysUntil = (dateStr) => {
    const renewalDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    renewalDate.setHours(0, 0, 0, 0);
    return differenceInDays(renewalDate, today);
  };

  const getStatusBadge = (task) => {
    if (task.is_renewed) {
      return { class: "badge-success", text: "Renewed" };
    }
    const daysUntil = getDaysUntil(task.renewal_date);
    if (daysUntil < 0) return { class: "badge-overdue", text: `${Math.abs(daysUntil)}d overdue` };
    if (daysUntil === 0) return { class: "badge-urgent", text: "Today" };
    if (daysUntil === 1) return { class: "badge-urgent", text: "Tomorrow" };
    if (daysUntil <= 3) return { class: "badge-warning", text: `${daysUntil} days` };
    return { class: "badge-normal", text: `${daysUntil} days` };
  };

  // Filter tasks by search term
  const filteredTasks = tasks.filter((task) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      task.customer_name.toLowerCase().includes(search) ||
      task.lyca_mobile_number.includes(search)
    );
  });

  // Sort tasks: overdue first, then by days until renewal
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (a.is_renewed !== b.is_renewed) return a.is_renewed ? 1 : -1;
    const daysA = getDaysUntil(a.renewal_date);
    const daysB = getDaysUntil(b.renewal_date);
    return daysA - daysB;
  });

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 text-[#00D26A] animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="page-container" data-testid="renewal-tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-white" data-testid="tasks-title">
            Renewal Tasks
          </h1>
          <p className="text-slate-400 mt-1">Manage plan renewal tasks</p>
        </div>
        <div className="flex items-center gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleGenerateTasks}
                className="btn-secondary flex items-center gap-2"
                data-testid="generate-tasks-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Generate Tasks
                <HelpCircle className="w-3 h-3 ml-1 opacity-60" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs bg-slate-800 border-slate-700 text-slate-200">
              <p className="text-sm">
                <strong>Usually not needed.</strong> Tasks are auto-created when customers are added 
                and when renewals are marked complete. Use this only to catch up on missing tasks.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            type="text"
            placeholder="Search by customer name or mobile number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 form-input"
            data-testid="search-tasks-input"
          />
        </div>
        
        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm text-slate-400">Status:</span>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 form-input" data-testid="filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="pending" className="text-slate-200 hover:bg-slate-800">Pending Only</SelectItem>
            <SelectItem value="all" className="text-slate-200 hover:bg-slate-800">All Tasks</SelectItem>
          </SelectContent>
        </Select>
        
        <span className="text-sm text-slate-500 ml-auto">
          Showing {sortedTasks.length} of {tasks.length} task{tasks.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Tasks Table */}
      <div className="card" data-testid="tasks-table-section">
        <div className="card-content p-0">
          {sortedTasks.length === 0 ? (
            <div className="empty-state py-12">
              <ClipboardList className="w-12 h-12 text-slate-600 mb-4" />
              <p className="text-lg">No tasks found</p>
              <p className="text-sm">
                {searchTerm 
                  ? "No tasks match your search. Try a different term."
                  : filter === "pending"
                    ? "All renewals are up to date!"
                    : "No renewal tasks have been created yet."}
              </p>
            </div>
          ) : (
            <table className="data-table" data-testid="tasks-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Mobile Number</th>
                  <th>Plan Type</th>
                  <th>Price</th>
                  <th>Renewal Date</th>
                  <th>Plan Renewed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTasks.map((task) => {
                  const status = getStatusBadge(task);
                  return (
                    <tr
                      key={task.id}
                      className={task.is_renewed ? "opacity-60" : ""}
                      data-testid={`task-row-${task.id}`}
                    >
                      <td>
                        <span className={`badge ${status.class}`}>{status.text}</span>
                      </td>
                      <td className="font-medium text-white">{task.customer_name}</td>
                      <td className="font-mono">{task.lyca_mobile_number}</td>
                      <td>{task.plan_type_name}</td>
                      <td className="font-mono">€{task.plan_price.toFixed(2)}</td>
                      <td>
                        <div>
                          <div>{format(new Date(task.renewal_date), "dd MMM yyyy")}</div>
                          {task.is_renewed && task.renewed_at && (
                            <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Renewed {format(new Date(task.renewed_at), "dd MMM")}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={task.is_renewed}
                            onCheckedChange={(checked) => handleMarkRenewed(task.id, checked)}
                            className="w-6 h-6 border-2 border-slate-600 data-[state=checked]:bg-[#00D26A] data-[state=checked]:border-[#00D26A]"
                            data-testid={`task-checkbox-${task.id}`}
                          />
                          {task.is_renewed && (
                            <span className="text-xs text-[#00D26A]">✓</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClick(task)}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-800"
                                data-testid={`edit-task-${task.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit task</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(task)}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-red-400 hover:bg-slate-800"
                                data-testid={`delete-task-${task.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete task</TooltipContent>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Info Card */}
      <div className="mt-6 card p-4 border-[#0066FF]/30 bg-[#0066FF]/5">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#0066FF]/20 flex items-center justify-center flex-shrink-0">
            <ClipboardList className="w-4 h-4 text-[#0066FF]" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white">How it works</h4>
            <p className="text-xs text-slate-400 mt-1">
              <strong>Mark Renewed:</strong> When a renewal is completed, check the box. An SMS notification is sent to the customer and the next renewal date is set to 27 days later.
            </p>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle>Edit Renewal Task</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update the details for this renewal task.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Customer Name</label>
              <Input
                value={editForm.customer_name}
                onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Mobile Number</label>
              <Input
                value={editForm.lyca_mobile_number}
                onChange={(e) => setEditForm({ ...editForm, lyca_mobile_number: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Plan Type</label>
              <Input
                value={editForm.plan_type_name}
                onChange={(e) => setEditForm({ ...editForm, plan_type_name: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Price (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.plan_price}
                  onChange={(e) => setEditForm({ ...editForm, plan_price: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Renewal Date</label>
                <Input
                  type="date"
                  value={editForm.renewal_date}
                  onChange={(e) => setEditForm({ ...editForm, renewal_date: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Renewal Task</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to delete this renewal task for <strong className="text-white">{deletingTask?.customer_name}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
}
